import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import scrollTo from 'commons/scrollTo';
import browserStorage from 'components/consumer/browser-storage';
import GridWithSidebar from 'components/consumer/GridWithSidebar';
import MobileOnly from 'components/consumer/MobileOnly';
import TabletAndDesktopOnly from 'components/consumer/TabletAndDesktopOnly';
import { usePrevious } from 'react-use';
import useScreen from 'hooks/useScreen';
import CollectionsGridEmpty from './CollectionsGridEmpty';
import CollectionsGridDesktop from './CollectionsGridDesktop';
import CollectionsGridMobile from './CollectionsGridMobile';

const CollectionsGrid = ({ clearFilters, collections, filters, loading }) => {
  const prevCollections = usePrevious(collections);
  const { isMobile } = useScreen();

  useEffect(() => {
    if (
      prevCollections &&
      collections &&
      collections.length > prevCollections.length
    ) {
      if (!isMobile) {
        const prevScrollPosition = browserStorage().getItem(
          'scrollPosition.collections'
        );
        if (prevScrollPosition) {
          scrollTo(0, parseInt(prevScrollPosition, 10));
          browserStorage().setItem('scrollPosition.collections', 0);
        }
      }
    }
  }, [collections, isMobile, prevCollections]);

  const getCustomProps = useCallback(
    selectedGridItem => {
      if (!selectedGridItem) {
        return {};
      }

      return {
        selectedItemName: selectedGridItem.name,
        selectedItemMaterial: selectedGridItem.name,
        shopThisText: 'Shop This Collection',
        shopThisTextLink: `${
          selectedGridItem.url && !selectedGridItem.url.startsWith('/')
            ? '/'
            : ''
        }${selectedGridItem.url}`,
        shopThisTextClick: () => {
          if (!isMobile) {
            if (selectedGridItem) {
              browserStorage().setItem(
                'selectedCollectionItem',
                JSON.stringify(selectedGridItem)
              );
              browserStorage().setItem(
                'collections.filters',
                JSON.stringify(filters)
              );
            } else {
              browserStorage().removeItem('selectedCollectionItem');
              browserStorage().removeItem('scrollPosition.collections');
              browserStorage().removeItem('collections.filters');
            }
          }
        },
        selectedItemDescription:
          selectedGridItem.category_description ||
          selectedGridItem.category_meta_description,
      };
    },
    [filters, isMobile]
  );

  const renderGrid = useCallback(
    ({ selectedGridItem, updateSelectedGridItem }) => {
      if (collections.length === 0 && !loading) {
        return <CollectionsGridEmpty clearFilters={clearFilters} />;
      }

      return (
        <>
          <MobileOnly>
            <CollectionsGridMobile
              collections={collections}
              loading={loading}
              setSelectedCollection={updateSelectedGridItem}
            />
          </MobileOnly>

          <TabletAndDesktopOnly>
            <CollectionsGridDesktop
              collections={collections}
              loading={loading}
              selectedCollection={selectedGridItem}
              setSelectedCollection={updateSelectedGridItem}
            />
          </TabletAndDesktopOnly>
        </>
      );
    },
    [clearFilters, collections, loading]
  );

  return (
    <GridWithSidebar
      gridItemType="collection"
      loading={loading}
      renderGrid={renderGrid}
      getCustomProps={getCustomProps}
    />
  );
};

CollectionsGrid.propTypes = {
  clearFilters: PropTypes.func,
  collections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  filters: PropTypes.object,
  loading: PropTypes.bool,
};

export default CollectionsGrid;
